import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  api, ApiError, fetchBlobUrl, uploadFile,
  type ClientContact, type ClientDetail, type ClientDoc, type ClientFields, type ClientHistory, type ClientVersion,
  type DirectoryUser, type DocCategorie, type DocTip, type Extracted, type UploadResult,
} from '../../api';
import { ErrorMsg, Modal } from '../../components/Modal';
import { useData } from '../../useData';
import {
  addressLine, CATEGORII, checkCnp, expiryStatus, fmtDate, prepareUpload, stitchImages, TIP_LABEL, TIPURI_STRADA,
} from '../../lib/clients';
import { countryOptions, formatPhone, joinPhone, splitPhone } from '../../lib/dialCodes';
import { findLocalitate, judetCode, useGeo, type Geo } from '../../lib/geo';

const EMPTY: ClientFields = {
  nume: '', prenume: null, numeAnterior: null, telefon: null, email: null, tipStrada: null, strada: null, numar: null, bloc: null,
  scara: null, etaj: null, apartament: null, localitate: null, judet: null, codPostal: null, alteDetaliiAdresa: null,
  responsabilUserId: null, dataNasterii: null, birthdayReminder: false, observatii: null,
};

/** Fișa clientului persoană fizică: nou (/a/:appId/nou?tip=PF) sau existent (/clienti/:id). */
export function ClientPage() {
  const params = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const geo = useGeo();
  const users = useData<DirectoryUser[]>('/directory/users').data ?? [];
  const clientId = params.id ? Number(params.id) : null;
  const [appId, setAppId] = useState<number | null>(params.appId ? Number(params.appId) : null);
  const tipClient = (search.get('tip') === 'PJ' ? 'PJ' : 'PF') as 'PF' | 'PJ';

  const [form, setForm] = useState<ClientFields>(EMPTY);
  const [contacts, setContacts] = useState<ClientContact[]>([]);
  const [detail, setDetail] = useState<ClientDetail | null>(null);
  const [docs, setDocs] = useState<ClientDoc[]>([]);
  const [can, setCan] = useState({ edit: true, delete: false, sensitive: true });
  const [cnp, setCnp] = useState('');
  const [editing, setEditing] = useState(!clientId);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!!clientId);
  const [pendingDocs, setPendingDocs] = useState<number[]>([]);
  const [lastExtract, setLastExtract] = useState<Extracted | null>(null);
  const [editingDoc, setEditingDoc] = useState<ClientDoc | null>(null);
  const [conflict, setConflict] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [adding, setAdding] = useState<{ categorie?: DocCategorie; denumire?: string } | null>(null);

  async function load() {
    if (!clientId) return;
    try {
      const r = await api.get<{ client: ClientDetail; documents: ClientDoc[]; contacts: ClientContact[]; can: typeof can }>(`/clients/${clientId}`);
      setDetail(r.client);
      setAppId(r.client.appId);
      const f = { ...EMPTY };
      for (const k of Object.keys(EMPTY) as (keyof ClientFields)[]) (f as Record<string, unknown>)[k] = r.client[k];
      setForm(f);
      setContacts(r.contacts);
      setDocs(r.documents);
      setCan(r.can);
      setCnp(r.client.cnp ?? '');
      setConflict(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const locked = !editing;
  const set = <K extends keyof ClientFields>(k: K, v: ClientFields[K]) => { setForm((f) => ({ ...f, [k]: v })); setSaved(false); };

  function changeCnp(v: string) {
    const digits = v.replace(/\D/g, '').slice(0, 13);
    setCnp(digits);
    setSaved(false);
    const c = checkCnp(digits);
    if (c.valid && c.birthDate) setForm((f) => ({ ...f, dataNasterii: c.birthDate! }));
  }

  /** Completează fișa din datele citite din act. */
  function applyExtract(ex: Extracted, overwrite: boolean) {
    setForm((f) => {
      const n = { ...f };
      const put = (k: keyof ClientFields, v: string | undefined) => {
        if (v && (overwrite || !n[k])) (n as Record<string, unknown>)[k] = v;
      };
      if (ex.tipDocument !== 'CAD') { put('nume', ex.nume); put('prenume', ex.prenume); put('dataNasterii', ex.dataNasterii); }
      const a = ex.adresa;
      if (a) {
        put('tipStrada', a.tipStrada); put('strada', a.strada); put('numar', a.numar); put('bloc', a.bloc); put('scara', a.scara);
        put('etaj', a.etaj); put('apartament', a.apartament); put('judet', a.judet);
        const found = geo ? findLocalitate(geo, a.judet ?? n.judet, a.localitate) : undefined;
        put('localitate', found?.[0] ?? a.localitate);
        put('codPostal', a.codPostal ?? (found?.[1] || undefined));
      }
      return n;
    });
    if (ex.cnp && (overwrite || !cnp)) setCnp(ex.cnp);
    setSaved(false);
  }

  async function onUpload(files: File[], tip?: DocTip, denumire?: string) {
    setError(null);
    let file = files[0];
    if (files.length > 1) {
      if (files.every((f) => f.type.startsWith('image/'))) file = await stitchImages(files.slice(0, 2));
      else throw new Error('Pentru față + verso alegeți două poze, sau un singur PDF cu ambele pagini.');
    }
    const { blob, name } = await prepareUpload(file);
    if (blob.size > 10 * 1024 * 1024) throw new Error('Fișierul are peste 10 MB.');
    const url = clientId ? `/clients/${clientId}/documents` : `/apps/${appId}/documents`;
    const q = (tip ? `&tip=${tip}` : '') + (denumire ? `&denumire=${encodeURIComponent(denumire)}` : '');
    const r = await uploadFile<UploadResult>(url, blob, name, q);
    if (clientId) await load();
    else {
      setDocs((d) => [r.document, ...d.map((x) => sameSeries(x, r.document) ? { ...x, isCurrent: false } : x)]);
      setPendingDocs((p) => [...p, Number(r.document.id)]);
    }
    if (r.extracted && r.extracted.tipDocument !== 'AD') {
      setLastExtract(r.extracted);
      if (!clientId) applyExtract(r.extracted, false);
    } else if (r.extractError) {
      setError(`Documentul a fost salvat, dar datele nu au putut fi citite automat: ${r.extractError}`);
    }
    return r;
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const body = {
        ...form, tipClient: detail?.tipClient ?? tipClient,
        cnp: can.sensitive ? cnp : undefined,
        rowVer: detail?.rowVer ?? undefined,
        documentIds: pendingDocs,
        contacts: contacts.filter((c) => c.valoare.trim()),
      };
      if (clientId) {
        await api.put(`/clients/${clientId}`, body);
        setPendingDocs([]);
        await load();
        setEditing(false);
        setSaved(true);
      } else {
        const r = await api.post<{ id: number }>(`/apps/${appId}/clients`, body);
        navigate(`/clienti/${r.id}`, { replace: true });
      }
    } catch (e) {
      setError((e as Error).message);
      if (e instanceof ApiError && e.code === 'conflict') setConflict(true);
    } finally {
      setBusy(false);
    }
  }

  const cnpCheck = cnp ? checkCnp(cnp) : null;
  const title = clientId ? [form.nume, form.prenume].filter(Boolean).join(' ') || 'Client' : 'Client nou — persoană fizică';
  const phones = contacts.map((c, i) => [c, i] as const).filter(([c]) => c.tip === 'Telefon');
  const emails = contacts.map((c, i) => [c, i] as const).filter(([c]) => c.tip === 'Email');
  const updateContact = (i: number, v: string) => { setContacts((cs) => cs.map((c, j) => (j === i ? { ...c, valoare: v } : c))); setSaved(false); };
  const removeContact = (i: number) => { setContacts((cs) => cs.filter((_, j) => j !== i)); setSaved(false); };
  const responsabil = users.find((u) => u.id === form.responsabilUserId);

  if (loading) return <p className="muted">Se încarcă…</p>;

  return (
    <div className={`client-page ${locked ? 'locked' : 'editing'}`}>
      <div className="page-head">
        <div>
          {appId && <button className="link small" onClick={() => navigate(`/a/${appId}`)}>← Clienți</button>}
          <h1>{title} {clientId && <span className="client-id">ID {String(clientId).padStart(6, '0')}</span>}</h1>
        </div>
        <div className="actions-inline">
          {clientId && locked && <button className="link" onClick={() => setShowHistory(true)}>Istoric</button>}
          {clientId && locked && can.delete && (
            <button className="link danger" onClick={async () => {
              if (!window.confirm(`Arhivați clientul ${title}?`)) return;
              await api.del(`/clients/${clientId}`);
              navigate(`/a/${appId}`);
            }}>Arhivează</button>
          )}
          {clientId && locked && can.edit && <button className="primary" onClick={() => { setEditing(true); setSaved(false); }}>✎ Editează</button>}
          {clientId && !locked && <button onClick={() => { setEditing(false); load(); }}>Renunță</button>}
          {!locked && <button className="primary" disabled={busy || !form.nume.trim()} onClick={save}>{busy ? 'Se salvează…' : clientId ? 'Salvează' : 'Creează clientul'}</button>}
        </div>
      </div>
      <ErrorMsg error={error} />
      {conflict && (
        <p className="warn-text">
          Ce ați scris rămâne pe ecran până reîncărcați: copiați ce aveți nevoie, apoi{' '}
          <button className="link" onClick={() => { setError(null); load(); }}>reîncărcați fișa</button>.
        </p>
      )}
      {saved && <p className="tag ok">Modificările au fost salvate.</p>}

      {!clientId && (
        <UploadZone onFiles={(f) => onUpload(f)} big multiple
          title="Încărcați actul de identitate"
          hint="Carte de identitate (simplă sau electronică), pașaport sau permis — poză sau PDF. Pentru cartea electronică selectați ambele poze (față și verso). Datele se completează automat; verificați-le înainte de salvare." />
      )}

      {lastExtract && (
        <ExtractNotice ex={lastExtract} existing={!!clientId}
          onApply={() => { setEditing(true); applyExtract(lastExtract, true); setLastExtract(null); }}
          onClose={() => setLastExtract(null)} />
      )}

      <Section title="Informații personale">
        <div className="grid g3">
          <Field label="Nume *"><TextIn value={form.nume} onChange={(v) => set('nume', v)} locked={locked} /></Field>
          <Field label="Prenume"><TextIn value={form.prenume} onChange={(v) => set('prenume', v)} locked={locked} /></Field>
          <Field label="Nume anterior"><TextIn value={form.numeAnterior} onChange={(v) => set('numeAnterior', v)} locked={locked} /></Field>
        </div>
        <div className="grid g2">
          <Field label="CNP">
            {can.sensitive ? (
              <div className="cnp-row">
                <input className="mono" maxLength={13} inputMode="numeric" value={cnp} readOnly={locked}
                  onChange={(e) => changeCnp(e.target.value)} placeholder={locked ? '' : '13 cifre'} />
                {cnp && !locked && <button type="button" className="link icon" title="Șterge CNP-ul" onClick={() => changeCnp('')}>🗑</button>}
              </div>
            ) : (
              <input readOnly value={detail?.hasCnp ? 'completat (nu aveți drept de vizualizare)' : ''} />
            )}
            {cnpCheck?.valid && <span className="ok-text small">✓ CNP valid · data nașterii {fmtDate(cnpCheck.birthDate)}</span>}
            {cnp && cnpCheck && !cnpCheck.valid && <span className="error-text small">✗ {cnp.length < 13 ? `CNP incomplet (${cnp.length}/13 cifre)` : cnpCheck.reason}</span>}
          </Field>
          <Field label="Data nașterii">
            {locked
              ? <input readOnly value={form.dataNasterii ? fmtDate(form.dataNasterii) : ''} />
              : (
                <div className="cnp-row">
                  <input type="date" value={form.dataNasterii ?? ''} onChange={(e) => set('dataNasterii', e.target.value || null)} />
                  {form.dataNasterii && <button type="button" className="link icon" title="Șterge data" onClick={() => set('dataNasterii', null)}>🗑</button>}
                </div>
              )}
            {cnpCheck?.valid && form.dataNasterii && cnpCheck.birthDate !== form.dataNasterii && (
              <span className="warn-text small">Data diferă de cea din CNP ({fmtDate(cnpCheck.birthDate)}).</span>
            )}
          </Field>
        </div>

        <div className="grid g2">
          <div>
            <Field label="Telefon"><PhoneInput value={form.telefon} onChange={(v) => set('telefon', v)} locked={locked} /></Field>
            {phones.map(([c, i]) => (
              <div key={i} className="extra-field">
                <PhoneInput value={c.valoare} onChange={(v) => updateContact(i, v ?? '')} locked={locked} />
                {!locked && <button type="button" className="link icon" title="Elimină" onClick={() => removeContact(i)}>🗑</button>}
              </div>
            ))}
            {!locked && <button type="button" className="link add-more" onClick={() => setContacts((c) => [...c, { tip: 'Telefon', valoare: '', eticheta: null }])}>+ Adaugă telefon</button>}
          </div>
          <div>
            <Field label="Email"><TextIn type="email" value={form.email} onChange={(v) => set('email', v)} locked={locked} /></Field>
            {emails.map(([c, i]) => (
              <div key={i} className="extra-field">
                <TextIn type="email" value={c.valoare} onChange={(v) => updateContact(i, v)} locked={locked} />
                {!locked && <button type="button" className="link icon" title="Elimină" onClick={() => removeContact(i)}>🗑</button>}
              </div>
            ))}
            {!locked && <button type="button" className="link add-more" onClick={() => setContacts((c) => [...c, { tip: 'Email', valoare: '', eticheta: null }])}>+ Adaugă e-mail</button>}
          </div>
        </div>

        <hr />
        <div className="subhead"><strong>ADRESĂ</strong> <span className="muted">preluată automat din actul de identitate / certificatul de domiciliu, sau completată manual</span></div>
        <LocalitateJudet geo={geo} form={form} locked={locked} onChange={(patch) => { setForm((f) => ({ ...f, ...patch })); setSaved(false); }} />
        <div className="grid addr2">
          <Field label="Tip">
            {locked ? <input readOnly value={form.tipStrada ?? ''} /> : (
              <select value={form.tipStrada ?? ''} onChange={(e) => set('tipStrada', e.target.value || null)}>
                <option value="">—</option>
                {TIPURI_STRADA.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
          </Field>
          <Field label="Denumire"><TextIn value={form.strada} onChange={(v) => set('strada', v)} locked={locked} /></Field>
          <Field label="Număr"><TextIn value={form.numar} onChange={(v) => set('numar', v)} locked={locked} /></Field>
          <Field label="Bloc"><TextIn value={form.bloc} onChange={(v) => set('bloc', v)} locked={locked} /></Field>
          <Field label="Scara"><TextIn value={form.scara} onChange={(v) => set('scara', v)} locked={locked} /></Field>
          <Field label="Etaj"><TextIn value={form.etaj} onChange={(v) => set('etaj', v)} locked={locked} /></Field>
          <Field label="Apartament"><TextIn value={form.apartament} onChange={(v) => set('apartament', v)} locked={locked} /></Field>
        </div>
        <Field label="Alte detalii adresă">
          <TextIn value={form.alteDetaliiAdresa} onChange={(v) => set('alteDetaliiAdresa', v)} locked={locked} placeholder="ex. interfon 12, casa din spate, reper…" />
        </Field>
        {addressLine(form) && <p className="addr-line"><strong>În documente:</strong> {addressLine(form)}</p>}

        <hr />
        <div className="grid g2">
          <Field label="Responsabil client">
            {locked ? <input readOnly value={responsabil ? responsabil.displayName : ''} placeholder="—" /> : (
              <select value={form.responsabilUserId ?? ''} onChange={(e) => set('responsabilUserId', e.target.value ? Number(e.target.value) : null)}>
                <option value="">— neatribuit —</option>
                <optgroup label="Utilizatori">
                  {users.map((u) => <option key={u.id} value={u.id}>{u.displayName}</option>)}
                </optgroup>
                <optgroup label="Intermediari (urmează, din aplicația Colaboratori)" />
              </select>
            )}
          </Field>
        </div>
      </Section>

      <Section title="Documente" right={(clientId ? can.edit : true) ? <button className="primary small" onClick={() => setAdding({})}>+ Adaugă document</button> : undefined}>
        <DocumentList docs={docs} can={can} editable={clientId ? can.edit : true}
          onNewVersion={(d) => setAdding({ categorie: d.categorie, denumire: d.denumireDocument ?? undefined })}
          onEdit={setEditingDoc}
          onDelete={async (d) => {
            if (!window.confirm(`Ștergeți ${d.downloadName}?`)) return;
            await api.del(`/documents/${d.id}`);
            if (clientId) await load();
            else { setDocs((x) => x.filter((y) => y.id !== d.id)); setPendingDocs((p) => p.filter((y) => y !== d.id)); }
          }} />
        {!clientId && docs.length > 0 && <p className="muted small">Documentele se atașează clientului la salvare.</p>}
      </Section>

      <Section title="Observații" collapsed={!form.observatii}>
        <textarea rows={4} value={form.observatii ?? ''} onChange={(e) => set('observatii', e.target.value)} readOnly={locked} />
      </Section>

      {showHistory && clientId && (
        <HistoryModal clientId={clientId} rowVer={detail?.rowVer ?? null} onClose={() => setShowHistory(false)}
          onRestored={() => { setShowHistory(false); setPendingDocs([]); load(); }} />
      )}

      {adding && <AddDocModal initial={adding} onClose={() => setAdding(null)}
        onUpload={async (files, tip, den) => { await onUpload(files, tip, den); setAdding(null); }} />}
      {editingDoc && (
        <DocModal doc={editingDoc} onClose={() => setEditingDoc(null)}
          onSaved={() => { setEditingDoc(null); if (clientId) load(); }} />
      )}
    </div>
  );
}

function sameSeries(a: ClientDoc, b: ClientDoc) {
  return a.categorie === b.categorie && (a.categorie !== 'AD' || a.denumireDocument === b.denumireDocument);
}

// ---------------------------------------------------------------------------
// Câmpuri
// ---------------------------------------------------------------------------
function Section({ title, children, right, collapsed }: { title: string; children: ReactNode; right?: ReactNode; collapsed?: boolean }) {
  const [open, setOpen] = useState(!collapsed);
  return (
    <section className="card-section">
      <header onClick={() => setOpen(!open)}>
        <h2>{title}</h2>
        <span className="spacer" />
        <span onClick={(e) => e.stopPropagation()}>{right}</span>
        <span className="chev">{open ? '︿' : '﹀'}</span>
      </header>
      {open && <div className="body">{children}</div>}
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="field"><span className="flabel">{label}</span>{children}</label>;
}

/** Câmp text; în modul vizualizare e doar citire (textul se poate selecta și copia). */
function TextIn({ value, onChange, locked, type, placeholder }: {
  value: string | null; onChange: (v: string) => void; locked: boolean; type?: string; placeholder?: string;
}) {
  return <input type={type ?? 'text'} value={value ?? ''} readOnly={locked} placeholder={locked ? '' : placeholder}
    onChange={(e) => onChange(e.target.value)} />;
}

/** Telefon cu prefixul țării (listă cu steaguri). Valoarea: +40783069625. */
export function PhoneInput({ value, onChange, locked }: { value: string | null; onChange: (v: string | null) => void; locked?: boolean }) {
  const initial = splitPhone(value);
  const [iso, setIso] = useState(initial.iso);
  const [num, setNum] = useState(initial.number);
  const options = useMemo(countryOptions, []);
  useEffect(() => {
    const s = splitPhone(value);
    if (joinPhone(iso, num) !== (value || null)) { setIso(s.iso); setNum(s.number); }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  if (locked) return <input readOnly value={formatPhone(value)} />;
  const opt = options.find((o) => o.iso === iso);
  return (
    <div className="phone-input">
      <select value={iso} onChange={(e) => { setIso(e.target.value); onChange(joinPhone(e.target.value, num)); }} title="Prefixul țării">
        {options.map((o) => <option key={o.iso} value={o.iso}>{o.label}</option>)}
      </select>
      <span className="phone-code">{opt?.label.split(' ')[0]} {opt?.code}</span>
      <input type="tel" inputMode="tel" value={num} placeholder="7xx xxx xxx"
        onChange={(e) => { const v = e.target.value.replace(/[^0-9 ]/g, ''); setNum(v); onChange(joinPhone(iso, v)); }} />
    </div>
  );
}

/** Județ → Localitate → Cod poștal (nomenclatorul României). */
function LocalitateJudet({ geo, form, onChange, locked }: {
  geo: Geo | null; form: ClientFields; onChange: (p: Partial<ClientFields>) => void; locked: boolean;
}) {
  const code = geo ? judetCode(geo, form.judet) : undefined;
  const list = geo && code ? geo.localitati[code] ?? [] : [];
  const listId = `loc-${code ?? 'none'}`;
  const judetKnown = !form.judet || !!code;
  return (
    <div className="grid g3w">
      <Field label="Județ">
        {locked ? <input readOnly value={form.judet ?? ''} /> : (
          <select value={judetKnown ? (geo?.judete.find(([c]) => c === code)?.[1] ?? form.judet ?? '') : form.judet ?? ''} disabled={!geo}
            onChange={(e) => onChange({ judet: e.target.value || null, localitate: null, codPostal: null })}>
            <option value="">{geo ? '— alegeți —' : 'Se încarcă…'}</option>
            {!judetKnown && <option value={form.judet!}>{form.judet}</option>}
            {geo?.judete.map(([c, n]) => <option key={c} value={n}>{n}</option>)}
          </select>
        )}
      </Field>
      <Field label="Localitate">
        <input list={locked ? undefined : listId} value={form.localitate ?? ''} readOnly={locked} autoComplete="off"
          placeholder={locked ? '' : form.judet ? 'Căutați localitatea…' : 'Alegeți întâi județul'}
          onChange={(e) => {
            const v = e.target.value;
            const hit = list.find(([n]) => n === v);
            onChange({ localitate: v, ...(hit?.[1] ? { codPostal: hit[1] } : {}) });
          }} />
        {!locked && <datalist id={listId}>{list.map(([n]) => <option key={n} value={n} />)}</datalist>}
      </Field>
      <Field label="Cod poștal">
        <input value={form.codPostal ?? ''} readOnly={locked} inputMode="numeric" maxLength={6}
          onChange={(e) => onChange({ codPostal: e.target.value.replace(/\D/g, '') || null })} />
      </Field>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Documente
// ---------------------------------------------------------------------------
async function openDoc(d: ClientDoc, download: boolean) {
  // fereastra se deschide imediat (altfel browserul o blochează), apoi primește fișierul
  const w = download ? null : window.open('', '_blank');
  const url = await fetchBlobUrl(`/documents/${d.id}/file${download ? '?download=1' : ''}`);
  if (!url) { w?.close(); return; }
  if (download) {
    const a = document.createElement('a');
    a.href = url;
    a.download = d.downloadName;
    a.click();
  } else if (w) {
    w.location.href = url;
  }
}

function DocumentList({ docs, can, editable, onNewVersion, onEdit, onDelete }: {
  docs: ClientDoc[]; can: { sensitive: boolean }; editable: boolean;
  onNewVersion: (d: ClientDoc) => void; onEdit: (d: ClientDoc) => void; onDelete: (d: ClientDoc) => void;
}) {
  const [open, setOpen] = useState<Record<number, boolean>>({});
  // o serie = același tip (la „Alte documente”: aceeași denumire); se afișează doar categoriile cu documente
  const series = CATEGORII.flatMap((cat) => {
    const inCat = docs.filter((d) => d.categorie === cat.cod);
    const keys = [...new Set(inCat.map((d) => (cat.cod === 'AD' ? d.denumireDocument ?? '' : '')))];
    return keys.map((k) => {
      const all = inCat.filter((d) => cat.cod !== 'AD' || (d.denumireDocument ?? '') === k).sort((a, b) => b.versiune - a.versiune);
      return { cat, current: all.find((d) => d.isCurrent) ?? all[0], older: all.filter((d) => d !== (all.find((x) => x.isCurrent) ?? all[0])) };
    });
  });
  if (series.length === 0) return <p className="muted">Niciun document încă. Folosiți „+ Adaugă document”.</p>;
  return (
    <div className="doc-series">
      {series.map(({ cat, current: d, older }) => {
        const st = expiryStatus(d.dataExpirarii);
        return (
          <div key={d.id} className="doc-card">
            <div className="doc-main">
              <span className="doc-icon">{cat.icon}</span>
              <div className="doc-info">
                <div className="doc-title">
                  <strong>{cat.cod === 'AD' ? d.denumireDocument : TIP_LABEL[d.tipDocument]}</strong>
                  <span className="tag">v{d.versiune}</span>
                  {st && <span className={st.cls}>{st.label}</span>}
                </div>
                <div className="doc-meta">
                  {[d.serie, d.numar].filter(Boolean).length > 0 && <span>Serie/nr: <b className="mono">{[d.serie, d.numar].filter(Boolean).join(' ')}</b></span>}
                  {d.emisDe && <span>Emis de: {d.emisDe}</span>}
                  {d.dataEmiterii && <span>Emis: {fmtDate(d.dataEmiterii)}</span>}
                  {d.dataExpirarii && <span>Expiră: {fmtDate(d.dataExpirarii)}</span>}
                </div>
                <div className="doc-file muted small">{d.downloadName}</div>
              </div>
              <div className="doc-actions">
                {can.sensitive && <button className="link" onClick={() => openDoc(d, false)}>Vezi</button>}
                {can.sensitive && <button className="link" onClick={() => openDoc(d, true)}>Descarcă</button>}
                {editable && <button className="link" onClick={() => onNewVersion(d)}>Versiune nouă</button>}
                {editable && <button className="link" onClick={() => onEdit(d)}>Editează</button>}
                {editable && <button className="link danger" onClick={() => onDelete(d)}>Șterge</button>}
              </div>
            </div>
            {older.length > 0 && (
              <div className="doc-history">
                <button className="link small" onClick={() => setOpen((o) => ({ ...o, [d.id]: !o[d.id] }))}>
                  {open[d.id] ? '▾' : '▸'} Versiuni anterioare ({older.length})
                </button>
                {open[d.id] && (
                  <table className="docs">
                    <tbody>
                      {older.map((o) => (
                        <tr key={o.id}>
                          <td><span className="tag">v{o.versiune}</span> {TIP_LABEL[o.tipDocument]}</td>
                          <td className="mono">{[o.serie, o.numar].filter(Boolean).join(' ') || '—'}</td>
                          <td>{fmtDate(o.dataEmiterii)} – {fmtDate(o.dataExpirarii)}</td>
                          <td className="muted small">înlocuit {o.replacedAt ? new Date(o.replacedAt).toLocaleDateString('ro-RO') : ''}</td>
                          <td className="right nowrap">
                            {can.sensitive && <button className="link" onClick={() => openDoc(o, false)}>Vezi</button>}
                            {can.sensitive && <button className="link" onClick={() => openDoc(o, true)}>Descarcă</button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Alegerea tipului de document, apoi încărcarea (cu citire automată). */
function AddDocModal({ initial, onClose, onUpload }: {
  initial: { categorie?: DocCategorie; denumire?: string }; onClose: () => void;
  onUpload: (files: File[], tip: DocTip, denumire?: string) => Promise<void>;
}) {
  const [cat, setCat] = useState<DocCategorie | undefined>(initial.categorie);
  const [ciType, setCiType] = useState<'CI' | 'CEI'>('CEI');
  const [denumire, setDenumire] = useState(initial.denumire ?? '');
  const tip: DocTip | undefined = cat === 'CI' ? ciType : cat;
  const two = cat === 'CI' || cat === 'CN' || cat === 'CAD' || cat === 'PC';
  return (
    <Modal title={initial.categorie ? 'Versiune nouă a documentului' : 'Adaugă document'} onClose={onClose}>
      {!cat && (
        <div className="type-choice grid3">
          {CATEGORII.map((c) => (
            <button key={c.cod} onClick={() => setCat(c.cod)}><span className="tc-icon">{c.icon}</span><strong>{c.label}</strong></button>
          ))}
        </div>
      )}
      {cat && (
        <>
          <p><strong>{CATEGORII.find((c) => c.cod === cat)?.icon} {CATEGORII.find((c) => c.cod === cat)?.label}</strong>
            {!initial.categorie && <button className="link small" onClick={() => setCat(undefined)}>schimbă</button>}</p>
          {cat === 'CI' && (
            <div className="seg">
              <button className={ciType === 'CEI' ? 'on' : ''} onClick={() => setCiType('CEI')}>Carte electronică (CEI)</button>
              <button className={ciType === 'CI' ? 'on' : ''} onClick={() => setCiType('CI')}>Carte de identitate simplă</button>
            </div>
          )}
          {cat === 'AD' && (
            <label>Ce document este? *
              <input value={denumire} onChange={(e) => setDenumire(e.target.value)} placeholder="ex. Certificat de căsătorie" autoFocus readOnly={!!initial.denumire} />
            </label>
          )}
          {(cat !== 'AD' || denumire.trim()) && (
            <UploadZone multiple={two} onFiles={(f) => onUpload(f, tip!, cat === 'AD' ? denumire.trim() : undefined)}
              title="Alegeți poza sau PDF-ul"
              hint={cat === 'CI' && ciType === 'CEI'
                ? 'Selectați ambele poze — fața și versoul (data emiterii e pe verso) — sau un PDF cu două pagini.'
                : two ? 'Puteți selecta și două poze (față + verso).' : 'Poză (JPG, PNG) sau PDF.'} />
          )}
          {initial.categorie && <p className="muted small">Documentul nou devine versiunea curentă; cel vechi rămâne în „Versiuni anterioare”.</p>}
        </>
      )}
    </Modal>
  );
}

function UploadZone({ onFiles, title, hint, big, multiple }: {
  onFiles: (f: File[]) => Promise<unknown>; title: string; hint: string; big?: boolean; multiple?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  async function handle(list: FileList | null | undefined) {
    const files = list ? Array.from(list).slice(0, 2) : [];
    if (!files.length) return;
    setBusy(true);
    setError(null);
    try { await onFiles(files); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }
  return (
    <div className={`upload-zone ${big ? 'big' : ''} ${over ? 'over' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); handle(e.dataTransfer.files); }}
      onClick={() => !busy && input.current?.click()}>
      <input ref={input} type="file" accept="image/*,application/pdf" hidden multiple={multiple}
        onChange={(e) => { handle(e.target.files); e.target.value = ''; }} />
      <div className="uz-icon">🪪</div>
      <div>
        <strong>{busy ? 'Se încarcă și se citește documentul…' : title}</strong>
        <div className="muted small">{busy ? 'Durează câteva secunde.' : hint}</div>
        {error && <div className="error-text small">{error}</div>}
      </div>
    </div>
  );
}

function ExtractNotice({ ex, existing, onApply, onClose }: { ex: Extracted; existing: boolean; onApply: () => void; onClose: () => void }) {
  const a = ex.adresa;
  const rows: [string, string | undefined][] = [
    ['Tip', TIP_LABEL[ex.tipDocument]], ['Nume', [ex.nume, ex.prenume].filter(Boolean).join(' ')], ['CNP', ex.cnp],
    ['Serie / nr.', [ex.serie, ex.numar].filter(Boolean).join(' ')], ['Emis', ex.dataEmiterii && fmtDate(ex.dataEmiterii)],
    ['Expiră', ex.dataExpirarii && fmtDate(ex.dataExpirarii)],
    ['Adresă', a && [a.tipStrada, a.strada, a.numar && `nr. ${a.numar}`, a.localitate, a.judet].filter(Boolean).join(' ')],
  ];
  return (
    <div className="extract-notice">
      <div className="spread">
        <strong>Date citite din document</strong>
        <button className="link" onClick={onClose}>✕</button>
      </div>
      <div className="kv">{rows.filter((r) => r[1]).map(([k, v]) => <span key={k}><span className="muted">{k}:</span> {v}</span>)}</div>
      {ex.avertismente.map((w) => <p key={w} className="warn-text small">⚠ {w}</p>)}
      {existing
        ? <button className="link" onClick={onApply}>Actualizează fișa cu datele din document</button>
        : <p className="muted small">Câmpurile de mai jos au fost completate. Verificați-le înainte de salvare.</p>}
    </div>
  );
}

function DocModal({ doc, onClose, onSaved }: { doc: ClientDoc; onClose: () => void; onSaved: () => void }) {
  const [d, setD] = useState(doc);
  const [error, setError] = useState<string | null>(null);
  async function save() {
    try {
      await api.put(`/documents/${d.id}`, {
        tipDocument: d.tipDocument, denumireDocument: d.denumireDocument, serie: d.serie, numar: d.numar, emisDe: d.emisDe,
        dataEmiterii: d.dataEmiterii, dataExpirarii: d.dataExpirarii,
      });
      onSaved();
    } catch (e) { setError((e as Error).message); }
  }
  return (
    <Modal title={`Document v${d.versiune}`} onClose={onClose}>
      {d.categorie === 'CI' ? (
        <label>Tip
          <select value={d.tipDocument} onChange={(e) => setD({ ...d, tipDocument: e.target.value as DocTip })}>
            <option value="CEI">{TIP_LABEL.CEI}</option>
            <option value="CI">{TIP_LABEL.CI}</option>
          </select>
        </label>
      ) : d.categorie === 'AD' ? (
        <label>Ce document este?<input value={d.denumireDocument ?? ''} onChange={(e) => setD({ ...d, denumireDocument: e.target.value })} /></label>
      ) : <p><strong>{TIP_LABEL[d.tipDocument]}</strong></p>}
      <div className="grid g2">
        <label>Serie<input value={d.serie ?? ''} onChange={(e) => setD({ ...d, serie: e.target.value || null })} /></label>
        <label>Număr<input value={d.numar ?? ''} onChange={(e) => setD({ ...d, numar: e.target.value || null })} /></label>
      </div>
      <label>Emis de<input value={d.emisDe ?? ''} onChange={(e) => setD({ ...d, emisDe: e.target.value || null })} /></label>
      <div className="grid g2">
        <label>Data emiterii<input type="date" value={d.dataEmiterii ?? ''} onChange={(e) => setD({ ...d, dataEmiterii: e.target.value || null })} /></label>
        <label>Data expirării<input type="date" value={d.dataExpirarii ?? ''} onChange={(e) => setD({ ...d, dataExpirarii: e.target.value || null })} /></label>
      </div>
      <ErrorMsg error={error} />
      <div className="actions"><button onClick={onClose}>Renunță</button><button className="primary" onClick={save}>Salvează</button></div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Istoric: versiunile fișei (din istoricul automat al bazei de date) + documente șterse
// ---------------------------------------------------------------------------
const FIELD_LABEL: Record<string, string> = {
  Nume: 'nume', Prenume: 'prenume', NumeAnterior: 'denumire anterioară', Telefon: 'telefon', Email: 'e-mail',
  Strada: 'stradă', Numar: 'nr.', Bloc: 'bloc', Scara: 'scară', Etaj: 'etaj', Apartament: 'apartament',
  Localitate: 'localitate', Judet: 'județ', CodPostal: 'cod poștal', AlteDetaliiAdresa: 'alte detalii adresă',
  DataNasterii: 'data nașterii', Observatii: 'observații', Cnp: 'CNP', BirthdayNotifyUserId: 'anunț zi de naștere',
  BirthdayReminder: 'anunț zi de naștere', IsArchived: 'arhivare', Contacte: 'contacte suplimentare',
};
const fmtWhen = (iso: string | null) => (iso ? new Date(iso).toLocaleString('ro-RO', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

function versionSummary(v: ClientVersion): string[] {
  const c = v.client;
  return [
    [c.nume, c.prenume].filter(Boolean).join(' '),
    c.numeAnterior && `Denumire anterioară: ${c.numeAnterior}`,
    c.telefon && `Telefon: ${c.telefon}`,
    c.email && `E-mail: ${c.email}`,
    addressLine(c) && `Adresă: ${addressLine(c)}`,
    c.cnp ? `CNP: ${c.cnp}` : c.hasCnp ? 'CNP: completat' : null,
    c.dataNasterii && `Data nașterii: ${fmtDate(c.dataNasterii)}`,
    ...v.contacts.map((k) => `${k.tip === 'Telefon' ? 'Telefon' : 'E-mail'}${k.eticheta ? ` (${k.eticheta})` : ''}: ${k.valoare}`),
    c.observatii && `Observații: ${c.observatii}`,
  ].filter(Boolean) as string[];
}

function HistoryModal({ clientId, rowVer, onClose, onRestored }: {
  clientId: number; rowVer: string | null; onClose: () => void; onRestored: () => void;
}) {
  const [h, setH] = useState<ClientHistory | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get<ClientHistory>(`/clients/${clientId}/history`).then(setH).catch((e) => setError((e as Error).message));
  }, [clientId]);

  async function restore(v: ClientVersion) {
    if (!rowVer) return;
    if (!window.confirm(`Readuceți fișa la versiunea din ${fmtWhen(v.at)}? Versiunea actuală rămâne în istoric, deci și această restaurare se poate anula.`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/clients/${clientId}/restore`, { at: v.at, rowVer });
      onRestored();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  async function restoreDoc(id: number) {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/documents/${id}/restore`, {});
      onRestored();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <Modal title="Istoricul fișei" onClose={onClose}>
      <ErrorMsg error={error} />
      {!h && !error && <p className="muted">Se încarcă…</p>}
      {h && (
        <>
          <p className="muted small">Fiecare salvare păstrează automat versiunea anterioară. Restaurarea readuce datele fișei și contactele suplimentare.</p>
          <ul className="history">
            {h.versions.map((v) => (
              <li key={v.at}>
                <div className="spread">
                  <span>
                    <strong>{fmtWhen(v.at)}</strong>{v.by && <> · {v.by}</>}{' '}
                    {v.current && <span className="tag ok">actuală</span>}
                    <br />
                    <span className="muted small">
                      {v.first ? 'Fișa a fost creată' : v.changed.length ? `Modificat: ${v.changed.map((c) => FIELD_LABEL[c] ?? c).join(', ')}` : 'Documente atașate'}
                    </span>
                  </span>
                  <span className="nowrap">
                    <button className="link" onClick={() => setOpen(open === v.at ? null : v.at)}>{open === v.at ? 'Ascunde' : 'Vezi'}</button>
                    {!v.current && h.canRestore && <button className="link" disabled={busy || !rowVer} onClick={() => restore(v)}>Restaurează</button>}
                  </span>
                </div>
                {open === v.at && <div className="kv small">{versionSummary(v).map((line) => <span key={line}>{line}</span>)}</div>}
              </li>
            ))}
          </ul>
          {h.deletedDocuments.length > 0 && (
            <>
              <h3>Documente șterse</h3>
              <ul className="history">
                {h.deletedDocuments.map((d) => (
                  <li key={d.id} className="spread">
                    <span>
                      {TIP_LABEL[d.tipDocument]} {[d.serie, d.numar].filter(Boolean).join(' ')}
                      <br /><span className="muted small">șters {fmtWhen(d.deletedAt)}{d.deletedBy && ` de ${d.deletedBy}`}</span>
                    </span>
                    {h.canRestore && <button className="link" disabled={busy} onClick={() => restoreDoc(d.id)}>Readu documentul</button>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </Modal>
  );
}
